import {
  InputRequirement,
  Option,
  SurveyQuestion,
} from "@intelligems/ig-types/schemas/ai/nodes/input-requirement";
import {
  UserInput,
  UserInputAnswer,
} from "@intelligems/ig-types/schemas/ai/node-execution";

/**
 * Pure state helpers for the survey card, kept apart from React so the
 * stepper's rules — what counts as answered, where answering moves you, what
 * payload a finished card submits — are unit-testable without a provider tree.
 */

/** Longest prompt the card header will show verbatim — about three lines. */
export const SURVEY_CARD_TITLE_MAX_CHARS = 240;
export const SURVEY_CARD_TITLE_MAX_LINES = 3;

/** Header title when the ask's prompt is too long to be a card title. */
export const SURVEY_CARD_TITLE_FALLBACK = "Answer the question above";

/**
 * What the card header shows for a question. The header is a title of at
 * most about three lines, not a message body: a prompt within that budget is
 * shown as-is; anything longer (a legacy ask whose `prompt` is a whole
 * proposal, say) is replaced by a pointer at the transcript bubble that
 * already renders the full prompt, so a blob of text can never land in the
 * component.
 */
export function surveyCardTitle(prompt: string): string {
  const trimmed = prompt.trim();
  if (trimmed.length === 0) {
    return SURVEY_CARD_TITLE_FALLBACK;
  }
  const lines = trimmed.split("\n").length;
  if (
    trimmed.length > SURVEY_CARD_TITLE_MAX_CHARS ||
    lines > SURVEY_CARD_TITLE_MAX_LINES
  ) {
    return SURVEY_CARD_TITLE_FALLBACK;
  }
  return trimmed;
}

/**
 * Whether an ask's prompt is too long to title its card — the one case where
 * the transcript still carries the prompt, because the card header then
 * points at it ("Answer the question above"). A prompt that fits is the
 * card's title and is not repeated in the chat; an empty one has nothing to
 * show anywhere.
 */
export function isPromptTooLongForCardTitle(prompt: string): boolean {
  return (
    prompt.trim().length > 0 &&
    surveyCardTitle(prompt) === SURVEY_CARD_TITLE_FALLBACK
  );
}

/**
 * Every requirement the survey card renders, normalized to a question list.
 * A `survey` brings its own questions; a legacy `choice` or `confirmation`
 * is ONE question (the requirement's prompt) so the card renders identically
 * with the stepper simply absent. Anything else is not a card ask.
 */
export function deriveSurveyQuestions(
  requirement: InputRequirement
): SurveyQuestion[] | null {
  const { userInput } = requirement;
  switch (userInput.type) {
    case "survey":
      return userInput.questions;
    case "choice":
      if (userInput.options.length === 0) {
        return null;
      }
      return [
        {
          prompt: requirement.prompt ?? "",
          options: userInput.options,
          multiSelect: userInput.multiSelect,
          allowFreeText: userInput.allowFreeText,
        },
      ];
    case "confirmation":
      return [
        {
          prompt: requirement.prompt ?? "",
          options: [
            { value: "Yes", label: "Yes" },
            { value: "No", label: "No" },
          ],
        },
      ];
    default:
      return null;
  }
}

/** One option row as the survey card draws it. */
export type SurveyOptionRow = {
  option: Option;
  /** The row's title: the label, suffixed "(default)" on the default option. */
  title: string;
  /** The second line, when the card shows sublines at all. */
  subline?: string;
};

/**
 * The legacy default marker: before options had a `default` flag the agent
 * wrote the word into the `description`, and those asks persist in stored
 * transcripts. Read as the flag, never shown as a subline.
 */
const LEGACY_DEFAULT_DESCRIPTION = "default";

const isDefaultOption = (option: Option): boolean =>
  option.default === true ||
  option.description?.trim().toLowerCase() === LEGACY_DEFAULT_DESCRIPTION;

const sublineOf = (option: Option): string | undefined => {
  const description = option.description?.trim();
  if (!description) {
    return undefined;
  }
  if (description.toLowerCase() === LEGACY_DEFAULT_DESCRIPTION) {
    return undefined;
  }
  return description;
};

/**
 * The rows a question card draws (ENG-7739):
 *   - the default option says so in its title, in the title's own type —
 *     `3 (default)` — rather than as a subline, so it is the same height as
 *     its siblings;
 *   - sublines are all-or-none within a card: either every row has one or no
 *     row shows one, so the rows keep one height.
 */
export function surveyOptionRows(options: Option[]): SurveyOptionRow[] {
  const sublines = options.map(sublineOf);
  const showSublines =
    sublines.length > 0 && sublines.every((subline) => subline !== undefined);
  return options.map((option, index) => ({
    option,
    title: isDefaultOption(option) ? `${option.label} (default)` : option.label,
    subline: showSublines ? sublines[index] : undefined,
  }));
}

/**
 * Whether a requirement is asked on a card above the composer — every option
 * question (`survey`, `choice`, `confirmation`) and the `file` ask. A free-text
 * ask has no card: its prompt is only ever shown in the transcript.
 */
export function isCardAsk(requirement: InputRequirement): boolean {
  return (
    requirement.userInput.type === "file" ||
    deriveSurveyQuestions(requirement) !== null
  );
}

/** The composer's placeholder while a card's option rows are on screen. */
export const COMPOSER_OWN_ANSWER_PLACEHOLDER = "Or type your own answer...";

/** Longest question a placeholder quotes before it is cut short. */
const PLACEHOLDER_QUESTION_MAX_CHARS = 60;

/** What a card-shaped ask is currently asking — the stepper's question, for a survey. */
export function pendingQuestion(
  requirement: InputRequirement,
  currentIndex: number
): SurveyQuestion | { prompt: string } | null {
  const questions = deriveSurveyQuestions(requirement);
  if (questions) {
    return questions[currentIndex] ?? null;
  }
  const prompt = requirement.prompt?.trim();
  return prompt ? { prompt } : null;
}

/**
 * The composer's placeholder, tied to the pending question (ENG-7739):
 *   - nothing pending — the surface's own placeholder;
 *   - option rows on screen — "Or type your own answer...", the one case the
 *     "Or" has something to refer to;
 *   - a question pending with no rows showing (a file ask, a product pick,
 *     the card held back while the question is still being written, a
 *     free-text ask) — a placeholder naming that question.
 */
export function composerPlaceholder({
  requirement,
  currentIndex,
  optionsOnScreen,
  fallback,
}: {
  requirement: InputRequirement;
  currentIndex: number;
  optionsOnScreen: boolean;
  fallback: string | undefined;
}): string | undefined {
  const question = pendingQuestion(requirement, currentIndex);
  if (!question) {
    return fallback;
  }
  if (optionsOnScreen && "options" in question) {
    return COMPOSER_OWN_ANSWER_PLACEHOLDER;
  }
  const prompt = question.prompt.trim().replace(/\s+/g, " ");
  const quoted =
    prompt.length > PLACEHOLDER_QUESTION_MAX_CHARS
      ? `${prompt.slice(0, PLACEHOLDER_QUESTION_MAX_CHARS).trimEnd()}…`
      : prompt;
  return `Reply to “${quoted}”`;
}

/**
 * Where answering question `from` moves the stepper: the next unanswered
 * question scanning forward, wrapping to the front for anything skipped by
 * going back. `null` means every question is answered — time to submit.
 */
export function nextUnansweredIndex(
  answers: Record<number, UserInputAnswer>,
  from: number,
  total: number
): number | null {
  for (let index = from + 1; index < total; index++) {
    if (!answers[index]) {
      return index;
    }
  }
  for (let index = 0; index < from; index++) {
    if (!answers[index]) {
      return index;
    }
  }
  return null;
}

/**
 * The canonical submission a completed question card sends: one entry per
 * question, in question order. The same shape covers one question and many.
 */
export function buildSurveyUserInput(
  questions: SurveyQuestion[],
  answers: Record<number, UserInputAnswer>
): UserInput {
  return {
    type: "answers",
    answers: questions.map(
      (question, index) =>
        answers[index] ?? { prompt: question.prompt, choices: [] }
    ),
  };
}

/**
 * What answering the current question does to the survey: records the answer,
 * and either advances to the next unanswered question (`submission: null`) or
 * — when that was the last unanswered one — produces the single `answers`
 * user input to submit.
 */
export function recordSurveyAnswer(
  questions: SurveyQuestion[],
  answers: Record<number, UserInputAnswer>,
  currentIndex: number,
  answer: UserInputAnswer
): {
  answers: Record<number, UserInputAnswer>;
  currentIndex: number;
  submission: UserInput | null;
} {
  const nextAnswers = { ...answers, [currentIndex]: answer };
  const next = nextUnansweredIndex(nextAnswers, currentIndex, questions.length);
  if (next === null) {
    return {
      answers: nextAnswers,
      currentIndex,
      submission: buildSurveyUserInput(questions, nextAnswers),
    };
  }
  return { answers: nextAnswers, currentIndex: next, submission: null };
}
